/**
 * 사진 인쇄 — 앱 화면 대신 사진만 인쇄한다(사진 1장 = 용지 1장, 용지에 맞춤).
 *
 * 진입점은 셋이다(사진 뷰어 툴바 · 메시지 우클릭/롱프레스 메뉴 · 서랍 타일). 전부 여기를 거친다.
 *
 * 방식: 인쇄하는 동안만 <body> 끝에 호스트를 붙이고 window.print()를 부른다. 호스트 안 <style>이
 * `@media print`에서 body의 다른 자식(#root·라이트박스 포털·토스트)을 전부 숨기므로, 호스트를 떼면
 * 인쇄 규칙도 함께 사라진다(index.css에 전역 인쇄 CSS를 남기지 않는다 — 도움말 인쇄가 그대로 동작한다).
 *
 * ⚠ iframe으로 바꾸지 말 것. iOS Safari는 iframe의 print()가 상위 페이지 전체를 인쇄한다.
 *   문서 안 방식은 웹·PWA·Tauri(WebView2) 모두 같은 경로다. Tauri 2는 window.print를 macOS에서만
 *   가로채고 Windows에서는 WebView2가 직접 인쇄 창을 띄우므로 capability 권한이 필요 없다.
 *
 * ⚠ 호스트는 `afterprint`에서만 뗀다. print()가 돌아온 직후에 떼면 (1) iOS는 print가 비동기라
 *   앱 화면이 인쇄되고 (2) Chrome은 인쇄 창에서 방향·여백을 바꿀 때 미리보기를 현재 DOM으로 다시
 *   만든다. afterprint가 오지 않는 환경을 위해 다음 인쇄 시작 시에도 남은 호스트를 지운다.
 *
 * 교차 출처(Tauri의 tauri.localhost ↔ 서버) 이미지도 <img>로만 그리므로 CORS와 무관하다.
 */

export const PRINT_IMAGE_FAILED_TOAST = '사진을 인쇄하지 못했습니다. 잠시 후 다시 시도해 주세요.';

const HOST_ID = 'ina-image-print-host';
const LOAD_TIMEOUT_MS = 20000;

// 이미지를 불러오는 동안의 연타 방지. print()가 돌아오면 푼다 —
// afterprint에 묶으면 그 이벤트가 오지 않는 브라우저에서 영구히 잠긴다.
let preparing = false;

// 페이지 높이가 정확히 100vh면 반올림 때문에 빈 페이지가 끼는 브라우저가 있어 2px 덜어 낸다.
const PRINT_CSS = `
#${HOST_ID} { display: none; }
@media print {
  @page { margin: 0; }
  html, body {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    background: #fff !important;
  }
  body > :not(#${HOST_ID}) { display: none !important; }
  #${HOST_ID} { display: block !important; }
  #${HOST_ID} .pg {
    box-sizing: border-box;
    height: calc(100vh - 2px);
    padding: 10mm;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    break-inside: avoid;
  }
  #${HOST_ID} .pg + .pg { break-before: page; }
  #${HOST_ID} img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}
`;

const removeHost = () => {
  document.getElementById(HOST_ID)?.remove();
};

const waitForImage = (img: HTMLImageElement): Promise<void> =>
  new Promise((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    const timer = window.setTimeout(() => reject(new Error('image load timeout')), LOAD_TIMEOUT_MS);
    img.addEventListener('load', () => { window.clearTimeout(timer); resolve(); }, { once: true });
    img.addEventListener('error', () => { window.clearTimeout(timer); reject(new Error('image load failed')); }, { once: true });
  });

/**
 * 사진을 인쇄 창으로 보낸다. 이미지를 하나라도 불러오지 못하면 아무것도 인쇄하지 않고 throw한다
 * (호출부가 PRINT_IMAGE_FAILED_TOAST를 띄운다).
 */
export async function printImages(urls: string[]): Promise<void> {
  if (urls.length === 0 || preparing) return;
  preparing = true;
  try {
    removeHost();

    const host = document.createElement('div');
    host.id = HOST_ID;
    host.setAttribute('aria-hidden', 'true');
    const style = document.createElement('style');
    style.textContent = PRINT_CSS;
    host.appendChild(style);

    const images = urls.map((url) => {
      const page = document.createElement('div');
      page.className = 'pg';
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'sync';
      img.src = url;
      page.appendChild(img);
      host.appendChild(page);
      return img;
    });
    document.body.appendChild(host);

    try {
      await Promise.all(images.map(waitForImage));
    } catch (err) {
      removeHost();
      throw err;
    }

    window.addEventListener('afterprint', removeHost, { once: true });
    try {
      window.print();
    } catch (err) {
      window.removeEventListener('afterprint', removeHost);
      removeHost();
      throw err;
    }
  } finally {
    preparing = false;
  }
}
